import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import useForm from '@sm/hooks/useForm';

import Button from '@components/custom/Button';
import Input from '@components/custom/Input';
import ModuleHeader from '@components/ModuleHeader';
import Spinner from '@components/custom/Spinner';
import Select from '@components/custom/Select';

import '@assets/css/wallets.css';

import { crudActions } from '@sm/services/crudActions';
import { showAlert } from '@sm/store/alertSlice';
import { addLogItem } from '@sm/utils/utils';

const CreateWallet = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const walletType = location?.state?.type;

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [walletFields, setWalletFields] = useState([]);
  const [isPendingChanges, setIsPendingChanges] = useState(false);
  const [logs, setLogs] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [merchantId, setMerchantId] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  const fields = {};
  const fieldRules = [];

  const { form, reinitialize, setField, validateForm } = useForm(fields, fieldRules);

  const addLog = useCallback((valueBefore, valueAfter, field, currency) => {
    const type = "WALLET_UPDATE";
    const updatedLogs = addLogItem(logs, type, valueBefore, valueAfter, field, merchantId, currency);
    setLogs(updatedLogs);
  }, [logs, merchantId]);

  const onValueChange = useCallback((event, fieldName) => {
    if (fieldName === 'selectedCurrency') {
      setSelectedCurrency(event.value);
      setLogs([]);
      return;
    }

    const { value } = event.target;
    addLog(form[fieldName], value, fieldName, selectedCurrency);
    setField(fieldName, value);
  }, [addLog, form, selectedCurrency, setField]);

  // Get lookups
  useEffect(() => {
    setIsLoading(true);

    crudActions.get(`v1/clients/lookups/wallets`)
      .then((response) => {
        setCurrencies(response?.currencies);
        setMerchantId(response?.merchantId);
        setCompanyId(response?.companyId);
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  const getFilteredCurrencies = useCallback(() => {
    if (!currencies) return;
    const isCrypto = walletType === 'crypto';
    return currencies.filter(item => item.offset !== String(isCrypto));
  }, [currencies, walletType]);

  useEffect(() => {
    if (!currencies.length) return;
    const filteredCurrencies = getFilteredCurrencies();
    setSelectedCurrency(filteredCurrencies[0].value);
  }, [currencies, getFilteredCurrencies]);

  // Get wallet fields when on currency change
  useEffect(() => {
    if (!selectedCurrency) return;
    
    crudActions.get(`v1/clients/wallet-data?currency=${selectedCurrency}&isAgent=false`)
      .then(response => {
        if (response) {
          const formFields = {};
          const formFieldRules = [];
          let isPending = false;
          response.walletInfoList?.forEach(field => {
            if (field.pendingValue) {
              isPending = true;
            }
            formFields[field.id] = field.pendingValue || field.value || "";
            formFieldRules.push({
              name: field.id,
              type: "isString",
              rules: {
                required: field.required || field.id !== "updatedDate",
                ...(field.minLength && { min: field.minLength }),
                ...(field.maxLength && { max: field.maxLength })
              }
            });
          });
          setWalletFields(response.walletInfoList);
          setIsPendingChanges(isPending);
          reinitialize(formFields, formFieldRules);
        }
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
      })
      .finally(() => {
        setIsLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedCurrency]);

  const submitWallet = () => {
    const isFormValid = validateForm();
    if (isFormValid) {
      setIsSubmitting(true);

      const walletFormData = form.data();
      delete walletFormData.updatedDate;

      const data = {
        companyId,
        clientId: merchantId,
        currency: selectedCurrency,
        isAgent: false,
        fields: walletFormData,
        changes: logs
      };
      crudActions.post(`v1/wallets`, data)
        .then(() => {
          dispatch(showAlert('success', 'The Wallet Info has successfully been submitted.'));
          setTimeout(() => {
            navigate(`/wallets`);
          }, 1500);
        })
        .catch(err => {
          dispatch(showAlert('danger', err?.message));
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  const isCrypto = walletFields.some(field => field.id === 'wallet');

  return (
    <div className='col-12 module my-account wallets'>
      <div className='component shadow mb-4 position-relative'>
        <ModuleHeader
          id='account_balance_wallet'
          title={ `Submit ${walletType === 'crypto' ? 'Crypto' : 'Fiat'} Wallet` }
        />

        {isLoading && <Spinner /> }
        
        <form autoComplete="off">
          <div className='row'>
            <div className='col-12 row-title'>
              <h5>Enter details</h5>
            </div>
            <div className='col-sm-12 col-md-6 col-lg-3 position-relative mb-4 mb-md-0'>
              <Select
                id='currency'
                label='Wallet currency'
                value={ selectedCurrency }
                disabled={ isLoading }
                onChange={ (e) => onValueChange(e, 'selectedCurrency') }
                options={ getFilteredCurrencies() }
              />
            </div>
            <div className='col-sm-12 col-md-6 col-lg-3  mb-4 mb-md-0'>
              <Input
                id='name'
                label='Name'
                error={ form.errors.has('name') }
                errorMessage={ form.errors.get('name') }
                value={ form.name || '' }
                disabled={ isPendingChanges }
                onChange={ (e) => onValueChange(e, 'name') }
              />
            </div>
            {isCrypto && (
              <div className='col-sm-12 col-md-6 col-lg-3  mb-4 mb-md-0'>
                <Input
                  id='wallet'
                  label='Wallet'
                  error={ form.errors.has('wallet') }
                  errorMessage={ form.errors.get('wallet') }
                  value={ form.wallet || '' }
                  disabled={ isPendingChanges }
                  onChange={ (e) => onValueChange(e, 'wallet') }
                />
              </div>
            )}
            {!isCrypto && (
              <div className='col-sm-12 col-md-6 col-lg-3  mb-4 mb-md-0'>
                <Input
                  id='address'
                  label='Company Address'
                  error={ form.errors.has('address') }
                  errorMessage={ form.errors.get('address') }
                  value={ form.address || '' }
                  disabled={ isPendingChanges }
                  onChange={ (e) => onValueChange(e, 'address') }
                />
              </div>
            )}
          </div>
          {!isCrypto && (
            <div className='row'>
              <div className='col-12 row-title'>
                <h5>Bank Information</h5>
              </div>
              <div className='col-sm-12 col-md-6 col-lg-3  mb-4 mb-md-0'>
                <Input
                  id='bankName'
                  label='Bank Name'
                  error={ form.errors.has('bankName') }
                  errorMessage={ form.errors.get('bankName') }
                  value={ form.bankName || '' }
                  disabled={ isPendingChanges }
                  onChange={ (e) => onValueChange(e, 'bankName') }
                />
              </div>
              <div className='col-sm-12 col-md-6 col-lg-3  mb-4 mb-md-0'>
                <Input
                  id='accountNumber'
                  label='Account Number'
                  error={ form.errors.has('accountNumber') }
                  errorMessage={ form.errors.get('accountNumber') }
                  value={ form.accountNumber || '' }
                  disabled={ isPendingChanges }
                  onChange={ (e) => onValueChange(e, 'accountNumber') }
                />
              </div>
              <div className='col-sm-12 col-md-6 col-lg-3  mb-4 mb-md-0'>
                <Input
                  id='routingNumber'
                  label='Routing Number'
                  error={ form.errors.has('routingNumber') }
                  errorMessage={ form.errors.get('routingNumber') }
                  value={ form.routingNumber || '' }
                  disabled={ isPendingChanges }
                  onChange={ (e) => onValueChange(e, 'routingNumber') }
                />
              </div>
            </div>
          )}
          {isPendingChanges && (
            <div className='row'>
              <div className='col mt-4'>
                <p className='pending-message'>* There are already pending changes for {selectedCurrency} wallet, waiting for approval.</p>
              </div>
            </div>
          )}
          <div className='row'>
            <div className='col-12 d-flex justify-content-end mt-4'>
              <Button
                label='Save Wallet'
                disabled={ isSubmitting || isPendingChanges }
                onClick={ () => submitWallet() }
              />
            </div>
          </div>
        </form>

      </div>
    </div>
  );
};

export default CreateWallet;